import * as am4core from '@amcharts/amcharts4/core';
import {
    ValueAxis,
    CategoryAxis,
    XYChart,
    ColumnSeries,
    CircleBullet,
    LabelBullet
} from '@amcharts/amcharts4/charts';
import { Legend } from '@amcharts/amcharts4/charts';
import * as _ from 'underscore';

import { ChartModel } from '../chart.model';
import { HeatChartOptionsModel } from './heat-chart-options.model';
import { DataPointModel } from '../data-point.model';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';
import { chartColors } from '../../enums/chart-colors.enum';


export class HeatChartModel extends ChartModel {
    readonly singleSeries: boolean = true;

    declare amchart: XYChart;
    declare options: HeatChartOptionsModel;
    xAxis: ValueAxis;
    percentLabel: am4core.Label;
    seriesLabel: am4core.Label;
    circleBullet: CircleBullet;
    labelBullet: LabelBullet;
    percentBullet: LabelBullet;
    countBullet: LabelBullet;

    yAxis: CategoryAxis;

    sortedPercentages: number[] = [];

    minDisplayPercentage: number = 39;
    maxDisplayPercentage: number = 85;
    minDataPercentage: number = 0;
    maxDataPercentage: number = 0;
    radiusVariable: number = 1;
    minBubbleRadius: number = 34;

    public initChart(element: HTMLElement): void {
        super.initChart(element, XYChart);

        this.amchart.maskBullets = false;

        const xAxis = this.amchart.xAxes.push(new CategoryAxis());
        this.yAxis = this.amchart.yAxes.push(new CategoryAxis());

        xAxis.dataFields.category = 'xValue';
        this.yAxis.dataFields.category = 'yValue';

        xAxis.title.text = this.options.xLabel;
        this.yAxis.title.text = this.options.yLabel;

        xAxis.renderer.grid.template.disabled = true;
        xAxis.renderer.inversed = true;
        xAxis.renderer.minGridDistance = 40;
        xAxis.renderer.labels.template.disabled = true;

        this.yAxis.renderer.grid.template.disabled = true;
        this.yAxis.renderer.inversed = true;
        this.yAxis.renderer.minGridDistance = 40;
        this.yAxis.renderer.labels.template.disabled = true;
        this.yAxis.title.truncate = true;

        this.amchart.events.on('sizechanged', (event) => {
            const chart = event.target;
            this.setRadiusFromHeight(chart.pixelHeight, chart.pixelWidth);
            this.setTooltipSize(chart.plotContainer.innerWidth);
        });

        this.amchart.legend = new Legend();
        this.amchart.legend.contentAlign = 'left';
        this.amchart.legend.valign = 'bottom';
        this.amchart.legend.fontSize = '0.75rem';
        this.amchart.legend.labels.template.text =
            `Prominence of [bold]${(this.series[0])['styles']?.prominenceGroupName}[/] in [bold]${this.series[0].label}[/]`;
        this.amchart.legend.labels.template.truncate = true;
        const prominenceGroupKeywords = (this.series[0])['styles']?.prominenceGroupKeywords?.toString().replace(/,/g, ', ');
        if (prominenceGroupKeywords) {
            this.amchart.legend.itemContainers.template.tooltip = new am4core.Tooltip();
            this.amchart.legend.itemContainers.template.tooltipText = prominenceGroupKeywords;
            this.amchart.legend.itemContainers.template.tooltip.label.background.fill = am4core.color(chartColors.agprBlue);
            this.amchart.legend.itemContainers.template.tooltip.label.fill = am4core.color(chartColors.agprWhite);
        }

        // make it wider to accommodate long prominence group name or folder name, though we cannot accommodate super long names
        this.amchart.legend.itemContainers.template.maxWidth = 450;
        this.amchart.legend.itemContainers.template.paddingLeft = 10;
        this.amchart.legend.itemContainers.template.paddingTop = 2;
        this.amchart.legend.itemContainers.template.paddingBottom = 2;
        this.amchart.legend.itemContainers.template.focusable = false;

        // setting up csv exporting fields and display format
        this.amchart.exporting.dataFields = {
            exportingLabel: 'label',
            exportingPercent: 'percent',
            count: 'count'
        };

        this.amchart.exporting.adapter.add('data', data => {
            for (let i = 0; i < data.data.length; i++) {
                data.data[i].exportingLabel = data.data[i].label + ' ' + this.options.additionalExportingLabel;
                data.data[i].exportingPercent = data.data[i].percent + '%';
            }
            return data;
        });

        if (this.options.responsive) {
            this.amchart.legend.itemContainers.template.maxWidth = 300;
            this.amchart.legend.dy = 20;

            this.amchart.events.on('sizechanged', ev => {
                const chart = ev.target;
                if (chart.pixelWidth <= 768) {
                    chart.legend.fontSize = '0.875rem';
                    chart.titles.template.fontSize = '0.875rem';
                } else {
                    chart.legend.fontSize = '1rem';
                    chart.titles.template.fontSize = '1rem';
                }
            });
        }
    }

    setRadiusFromHeight(pixelHeight: number, pixelWidth: number) {
        this.minBubbleRadius = 34;
        this.yAxis.title.maxWidth = 500;
        if (this.options.noPadding) {
            this.amchart.padding(8, 0, 0, 0);
        } else {
            this.amchart.padding(20, 20, 20, 20);
        }
        if (pixelHeight) {
            if (pixelHeight > 470) {
                this.setBulletFontSize(0.875, 0.75);
                this.setRadiusVariable(1);
                if (pixelWidth < 250) {
                    this.setBulletFontSize(0.688, 0.688);
                    this.setRadiusVariable(2.2);
                } else {
                    this.setBulletFontSize(0.875, 0.75);
                    this.setRadiusVariable(1);
                }
            } else if (pixelHeight <= 470 && pixelHeight > 360) {
                if (pixelWidth < 250) {
                    this.setBulletFontSize(0.688, 0.688);
                    this.setRadiusVariable(2.2);
                } else {
                    this.setBulletFontSize(0.813, 0.75);
                    this.setRadiusVariable(1.5);
                }
            } else if (pixelHeight <= 360 && pixelHeight > 250) {
                this.setBulletFontSize(0.688, 0.688);
                this.setRadiusVariable(2);
            } else if (pixelHeight <= 250 && pixelHeight > 220) {
                this.setBulletFontSize(0.625, 0.625);
                this.setRadiusVariable(2.5);
            } else if (pixelHeight <= 220 && pixelHeight > 200) {
                this.setBulletFontSize(0.625, 0.625);
                this.setRadiusVariable(3);
            } else if (pixelHeight <= 200) {
                this.minBubbleRadius = 14;
                this.yAxis.title.maxWidth = 110;
                this.amchart.padding(20, 25, 5, 25);
                this.setBulletFontSize(0.563, 0.563);
                this.setRadiusVariable(5);
            }
        }
    }

    setBulletFontSize(labelSize: number, percentSize: number) {
        if (this.labelBullet?.fontSize) {
            this.labelBullet.fontSize = labelSize + 'rem';
        }
        if (this.percentBullet?.fontSize) {
            this.percentBullet.fontSize = percentSize + 'rem';
        }
    }

    setRadiusVariable(radiusVariable: number) {
        if (this.circleBullet?.circle) {
            this.radiusVariable = radiusVariable;
            this.amchart.validateData();
        }
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setExportStyles(scale?: number, keepFontsize?: boolean): void {
        super.setExportStyles(scale, keepFontsize);
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetExportStyles(): void {
        super.unsetExportStyles();
    }

    /**
     * Updates the current presentation of data in the chart
     */
    updateData(data: DataPointModel[][]): void {
        // for prominence chart, if calculation is not completed, do not update data
        if (!this.options.responsive && this.options.prominenceStatus !== ProminenceRecalculationJobStatus.DONE) {
            return;
        }
        this.rawData = data;
        if (!data || !data[0] || !data[0][0]) {
            this.amchart.data = null;
            this.amchart.series.clear();

            return;
        }

        this.sortedPercentages = data[0].map(x => +x.percent).sort((x: number, y: number) => x - y);
        this.minDataPercentage = this.sortedPercentages[0];
        this.maxDataPercentage = this.sortedPercentages[this.sortedPercentages.length - 1];

        const dataPoint: DataPointModel = data[0][0];

        this.amchart.series.clear();

        const series = this.amchart.series.push(new ColumnSeries());
        series.dataFields.categoryX = 'xValue';
        series.dataFields.categoryY = 'yValue';
        series.dataFields.value = 'percent';
        series.sequencedInterpolation = true;
        series.defaultState.transitionDuration = 3000;

        const column = series.columns.template;
        column.strokeWidth = 2;
        column.strokeOpacity = 1;

        column.stroke = am4core.color(dataPoint.color);
        column.width = am4core.percent(95);
        column.height = am4core.percent(95);
        column.column.cornerRadius(3, 3, 3, 3);
        column.propertyFields.fill = 'color';
        column.adapter.add('fill', (fill, target) => {
            let colorMultiplier = 1;
            const originalColor = am4core.color(fill.toString());

            if (target?.dataItem?.values && target.dataItem.values['value']) {
                const percent: number = +target.dataItem.values['value']['value'];

                // Here we want the biggest value to take the darkest, original color (multiplier 0)
                // and the smaller values to get lightened
                // minDisplayPercentage = 1 means white, here we retain a light color instead of white for the lowest percentage
                const minDisplayPercentage = 0.95;
                const maxDisplayPercentage = 0;

                colorMultiplier = this.mapRangeOntoAnother(
                    this.minDataPercentage,
                    this.maxDataPercentage,
                    minDisplayPercentage,
                    maxDisplayPercentage,
                    percent,
                    false
                );
            }

            return originalColor.lighten(colorMultiplier);
        });

        // Set up bullet appearance
        this.circleBullet = new CircleBullet();
        series.bullets.push(this.circleBullet);

        this.circleBullet.circle.adapter.add('radius',
            (coveragePercentage) => {
                const newRadius = this.mapRangeOntoAnother(
                    this.minDataPercentage,
                    this.maxDataPercentage,
                    this.minDisplayPercentage,
                    this.maxDisplayPercentage,
                    +coveragePercentage,
                    true
                );

                // 34 is the minumun bubble size that can accomadate all label text (percent + count)
                return Math.max(Math.round(newRadius / this.radiusVariable), this.minBubbleRadius);
            }
        );

        this.circleBullet.circle.propertyFields.radius = 'percent'; // TODO : normalize to a range, define range in options?
        this.circleBullet.circle.fill = am4core.color('#000'); // TODO : Use white if color is dark
        this.circleBullet.circle.strokeWidth = 0;
        this.circleBullet.circle.fillOpacity = 0.7;
        this.circleBullet.interactionsEnabled = true;

        // setup the tooltip
        const tooltip = new am4core.Tooltip();
        tooltip.getFillFromObject = false;
        tooltip.background.fill = am4core.color(dataPoint.color);
        tooltip.fontSize = '0.75rem';
        tooltip.label.wrap = true;
        tooltip.background.cornerRadius = 8;

        this.circleBullet.tooltip = tooltip;

        column.tooltip = tooltip;
        // This will get replaced by the segment adapter tooltip but if we don't put something here it won't run at all
        // If we define it with text that text will appear as the tooltip in Interactive Shared Reports component
        column.tooltipHTML = null;


        if (!this.options.responsive) {
            this.circleBullet.tooltip.dy = -15;
            // This will get replaced by the segment adapter tooltip but if we don't put something here it won't run at all
            // If we define it with text that text will appear as the tooltip in Interactive Shared Reports component
            this.circleBullet.tooltipHTML = null;
        }

        if (this.options.showLabel) {
            this.labelBullet = new LabelBullet();
            const labelBullet = series.bullets.push(this.labelBullet);
            labelBullet.label.text = '{label}';
            labelBullet.label.fill = am4core.color('#fff'); // TODO :Pick between white and black depending on point.color
            labelBullet.zIndex = 1;
            labelBullet.dy = -5;
            labelBullet.fontSize = '0.875rem';
            labelBullet.fontWeight = 'bold';
            labelBullet.interactionsEnabled = true;

            if (!this.options.responsive) {
                const labelTooltip = new am4core.Tooltip();
                labelTooltip.getFillFromObject = false;
                labelTooltip.background.fill = am4core.color(dataPoint.color);
                labelTooltip.fontSize = '0.75rem';
                labelTooltip.label.wrap = true;
                labelTooltip.background.cornerRadius = 8;

                this.labelBullet.tooltip = labelTooltip;
                this.labelBullet.tooltip.dy = -10;
                // This will get replaced by the segment adapter tooltip but if we don't put something here it won't run at all
                // If we define it with text that text will appear as the tooltip in Interactive Shared Reports component
                this.labelBullet.tooltipHTML = null;
            }
        }

        if (this.options.showPercentage) {
            this.percentBullet = new LabelBullet();
            const percentBullet = series.bullets.push(this.percentBullet);
            percentBullet.label.text = '{percent}%';
            percentBullet.label.fill = am4core.color('#fff'); // TODO :Pick between white and black depending on point.color
            percentBullet.zIndex = 1;
            percentBullet.dy = 10;
            percentBullet.fontSize = '0.813rem';
            percentBullet.interactionsEnabled = true;

            if (!this.options.responsive) {
                const percentTooltip = new am4core.Tooltip();
                percentTooltip.getFillFromObject = false;
                percentTooltip.background.fill = am4core.color(dataPoint.color);
                percentTooltip.fontSize = '0.75rem';
                percentTooltip.background.cornerRadius = 8;

                this.percentBullet.tooltip = percentTooltip;
                // This will get replaced by the segment adapter tooltip but if we don't put something here it won't run at all
                // If we define it with text that text will appear as the tooltip in Interactive Shared Reports component
                this.percentBullet.tooltipHTML = null;
            }
        }

        if (this.options.showCount) {
            this.countBullet = new LabelBullet();
            const countBullet = series.bullets.push(this.countBullet);
            countBullet.label.text = '{count}';
            countBullet.label.fill = am4core.color('#fff'); // TODO :Pick between white and black depending on point.color
            countBullet.zIndex = 1;
            countBullet.dy = this.options.showPercentage ? 25 : 10;
            countBullet.fontSize = '0.75rem';
            countBullet.interactionsEnabled = true;

            if (!this.options.responsive) {
                const countTooltip = new am4core.Tooltip();
                countTooltip.getFillFromObject = false;
                countTooltip.background.fill = am4core.color(dataPoint.color);
                countTooltip.fontSize = '0.75rem';
                countTooltip.background.cornerRadius = 8;

                this.countBullet.tooltip = countTooltip;
                // This will get replaced by the segment adapter tooltip but if we don't put something here it won't run at all
                // If we define it with text that text will appear as the tooltip in Interactive Shared Reports component
                this.countBullet.tooltipHTML = null;
            }
        }

        this.amchart.data = data[0];

        const baseWidth = Math.min(this.amchart.plotContainer.maxWidth, this.amchart.plotContainer.maxHeight);
        const maxRadius = baseWidth / Math.sqrt(this.amchart.data.length) / 2 - 2; // 2 is jast a margin

        series.heatRules.push({ min: 10, max: maxRadius, property: 'radius', target: this.circleBullet.circle });

        this.setRadiusFromHeight(this.amchart.pixelHeight, this.amchart.pixelWidth);

        this.setTooltipSize(this.amchart.plotContainer.innerWidth);

        [this.countBullet, this.labelBullet, this.percentBullet, this.circleBullet].forEach(bullet => {
            if (bullet) {
                bullet.events.on('ready', (event) => {
                    event.target.tabindex = -1;
                });
            }
        });

        // text to use as aria-label for each quadrant
        let srText = '{label}, ';
        if (this.options.showCount) {
            srText += '{count}, ';
        }
        if (this.options.showPercentage) {
            srText += '{percent}%';
        }
        series.itemReaderText = srText;

        this.ready.next(true);
    }

    /**
     * Required but not currently used
     */
    updateSeriesStyles(): void {}

    /**
     * Exports data using amcharts
     */
    export(type: keyof am4core.IExportOptions, filename?: string): Promise<boolean> {

        return this.amchart.exporting.export(type);
    }

    public isContentEmpty(): boolean {
        return false;
    }

    // https://stackoverflow.com/questions/5731863/mapping-a-numeric-range-onto-another
    public mapRangeOntoAnother(
        inputStart: number,
        inputEnd: number,
        outputStart: number,
        outputEnd: number,
        targetNumber: number,
        applyRounding: boolean
    ): number {
        // for the case inputEnd equals inputStart, there's no range (all four quads have same weight)
        // we return outputStart
        if (inputEnd === inputStart) {
            return outputStart;
        }

        const slope = 1.0 * (outputEnd - outputStart) / (inputEnd - inputStart);

        if (applyRounding) {
            return outputStart + Math.round(slope * (targetNumber - inputStart));
        } else {
            return outputStart + slope * (targetNumber - inputStart);
        }
    }

    setTooltipSize(chartWidth: number) {
        const maxWidth = (chartWidth / 2) + 30;
        if (this.circleBullet) {
            this.circleBullet.tooltip.label.maxWidth = maxWidth;
        }
        if (this.labelBullet?.tooltip) {
            this.labelBullet.tooltip.label.maxWidth = maxWidth;
        }
    }
}
